import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/contexts/UserContext';
import { toast } from '@/hooks/use-toast';

export interface Wallet {
  id: string;
  profile_id: string;
  balance: number;
  pending_balance: number;
  escrow_balance: number;
  currency: string;
  status: 'active' | 'frozen' | 'suspended';
  created_at: string;
  updated_at: string;
}

export const useWallet = () => {
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { currentUser } = useUser();

  const fetchWallet = async () => {
    if (!currentUser?.id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase
        .from('user_wallets')
        .select('*')
        .eq('profile_id', currentUser.id)
        .maybeSingle();

      if (error) {
        throw error;
      }

      if (!data) {
        // Wallet doesn't exist, create it
        const { data: newWallet, error: createError } = await supabase
          .from('user_wallets')
          .insert({ profile_id: currentUser.id })
          .select()
          .single();

        if (createError) throw createError;
        setWallet(newWallet as Wallet);
      } else {
        setWallet(data as Wallet);
      }
    } catch (err) {
      console.error('Error fetching wallet:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch wallet');
      toast({
        title: 'Error',
        description: 'Failed to load wallet information',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWallet();

    // Set up realtime subscription
    const channel = supabase
      .channel('wallet-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_wallets',
          filter: `profile_id=eq.${currentUser?.id}`,
        },
        (payload) => {
          console.log('Wallet updated:', payload);
          setWallet(payload.new as Wallet);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentUser?.id]);

  return {
    wallet,
    loading,
    error,
    refetch: fetchWallet,
  };
};